import React, { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import './ShopifyProcessSections.css';

const STEPS: { title: string; body: ReactNode }[] = [
  {
    title: 'Onboarding and SKU Mapping',
    body: 'Connect your Shopify store, configure products, packaging preferences, and courier settings.',
  },
  {
    title: 'Inbound & Quality Control',
    body: (
      <>
        Send inventory to our{' '}
        <Link to="/our-fulfilment-center" className="shopify-inline-link">
          fulfilment centre UK
        </Link>
        , where products are received, quality checked, counted, and organised for efficient order processing.
      </>
    ),
  },
  {
    title: 'Real-Time Sync',
    body: 'Inventory synchronises with Shopify while orders import automatically with shipping preferences, customer notes, and fulfilment instructions.',
  },
  {
    title: 'Pick, Pack, Presentation',
    body: (
      <>
        Scanner-verified order picking with branded packaging, recyclable materials, and promotional inserts — supported
        by our{' '}
        <Link to="/contract-packing" className="shopify-inline-link">
          contract packing
        </Link>{' '}
        and kitting services for enhanced customer experience.
      </>
    ),
  },
  {
    title: 'Smart Courier Selection',
    body: 'Shipping labels are automatically generated using postcode, parcel dimensions, weight, and delivery SLA requirements.',
  },
  {
    title: 'Dispatch & Tracking',
    body: 'Multiple daily carrier collections with tracking automatically synced to Shopify for accurate customer notifications and delivery visibility.',
  },
  {
    title: 'Returns & Feedback Loop',
    body: (
      <>
        Scan, grade, restock, or quarantine returned items through our{' '}
        <Link to="/return-handling-services" className="shopify-inline-link">
          returns handling
        </Link>{' '}
        workflow while reporting trends to reduce preventable returns.
      </>
    ),
  },
];

const DIY_BENEFITS = [
  {
    title: 'Time back',
    body: 'Leadership can focus on growth, strategy, and customers instead of packing operations and seasonal staffing challenges.',
    icon: 'clock' as const,
  },
  {
    title: 'Lower total landed cost',
    body: 'Optimised packaging and carrier selection help reduce shipping costs without compromising delivery speed or service quality.',
    icon: 'cost' as const,
  },
  {
    title: 'Higher conversion',
    body: 'Fast next-day dispatch supports higher conversion rates, improved customer satisfaction, and increased repeat purchases.',
    icon: 'chart' as const,
  },
  {
    title: 'Operational resilience',
    body: 'Reliable fulfilment capacity and defined SLAs designed to manage seasonal peaks and sudden order volume increases.',
    icon: 'shield' as const,
  },
  {
    title: 'Multichannel simplicity',
    body: 'Centralised inventory management across Shopify, Amazon, eBay, and TikTok Shop from one shared stock pool.',
    icon: 'channels' as const,
  },
] as const;

const COURIERS = [
  { name: 'Royal Mail', detail: 'Tracked 24/48, Large Letter, International' },
  { name: 'DPD', detail: 'Next‑Day with Predict, weekend and timed options' },
  { name: 'DHL', detail: 'Domestic and international express' },
  { name: 'Evri', detail: 'Cost‑effective tracked economy services' },
  { name: 'UPS', detail: 'International and multi‑parcel consignments' },
] as const;

const SLAS = [
  {
    label: 'Dispatch',
    body: '99.9% order accuracy with reliable dispatch performance against agreed daily cut-off times.',
  },
  {
    label: 'Domestic',
    body: 'Royal Mail Tracked 24, Tracked 48, and DPD Next Day options support reliable delivery and customer tracking.',
  },
  {
    label: 'International',
    body: 'Economy and express shipping options available, including DDP services for international ecommerce fulfilment requirements.',
  },
  {
    label: 'Reporting',
    body: 'Real-time dashboards tracking on-time delivery, pick accuracy, returns analysis, and courier performance metrics.',
  },
] as const;

const USE_CASES: { title: string; body: ReactNode }[] = [
  {
    title: 'Scaling DTC brands',
    body: 'Eliminate fulfilment bottlenecks and maintain reliable service levels at scale.',
  },
  {
    title: 'TikTok Shop & live sellers',
    body: (
      <>
        Handle viral sales spikes efficiently with rapid order processing, batching, and dispatch workflows through
        our{' '}
        <Link to="/tiktok-shop-fulfillment-services" className="shopify-inline-link">
          TikTok Shop fulfilment
        </Link>{' '}
        service.
      </>
    ),
  },
  {
    title: 'Subscription & bundles',
    body: (
      <>
        Support kitting, promotional inserts, subscription orders, and accurate inventory management across every sales
        channel — ideal alongside{' '}
        <Link to="/seller-fulfilled-prime-services" className="shopify-inline-link">
          Seller Fulfilled Prime
        </Link>{' '}
        where applicable.
      </>
    ),
  },
  {
    title: 'International expansion',
    body: (
      <>
        Optimised packaging and shipping strategies designed to reduce costs and protect profit margins, coordinated
        with our{' '}
        <Link to="/logistics-services" className="shopify-inline-link">
          e-commerce logistics for sellers
        </Link>{' '}
        team.
      </>
    ),
  },
  {
    title: 'Seasonal peaks',
    body: 'Flexible fulfilment capacity that scales with demand without long-term operational overheads or fixed costs.',
  },
];

function BenefitIcon({ name }: { name: (typeof DIY_BENEFITS)[number]['icon'] }) {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none' as const, 'aria-hidden': true as const };
  switch (name) {
    case 'clock':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
          <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'cost':
      return (
        <svg {...common}>
          <path
            d="M12 3v18M7 8h7a3 3 0 100 6H9a3 3 0 110 6h7"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      );
    case 'chart':
      return (
        <svg {...common}>
          <path d="M4 18V8M10 18V4M16 18v-6M20 18H4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      );
    case 'shield':
      return (
        <svg {...common}>
          <path
            d="M12 3l8 4v5c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V7l8-4z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <rect x="3" y="5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="14" y="5" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
          <rect x="8.5" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        </svg>
      );
  }
}

const ShopifyProcessSections: React.FC = () => {
  return (
    <div className="shpd-root">
      <section className="shpd-section shpd-section--light" aria-labelledby="sh-how-title">
        <div className="shpd-container">
          <header className="shpd-header">
            <span className="shpd-eyebrow">Step by step</span>
            <h2 id="sh-how-title" className="shpd-title">
              How Shopify Fulfilment Works
            </h2>
            <p className="shpd-lead">
              A proven Shopify fulfilment UK workflow from store integration to returns management. Learn how to{' '}
              <Link to="/blog/how-to-choose-multi-channel-fulfilment-partner-uk-2026" className="shopify-inline-link">
                choose a fulfilment partner
              </Link>{' '}
              that fits your growth stage.
            </p>
          </header>

          <ol className="shpd-steps">
            {STEPS.map((step, i) => (
              <li key={step.title} className="shpd-step-card">
                <span className="shpd-step-num" aria-hidden>
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="shpd-step-body">
                  <h3 className="shpd-step-title">{step.title}</h3>
                  <p className="shpd-step-text">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="shpd-section shpd-section--blue" aria-labelledby="sh-diy-title">
        <div className="shpd-container">
          <header className="shpd-header shpd-header--on-blue">
            <span className="shpd-eyebrow shpd-eyebrow--light">Outsource vs in‑house</span>
            <h2 id="sh-diy-title" className="shpd-title shpd-title--light">
              Benefits vs Doing It Yourself
            </h2>
            <p className="shpd-lead shpd-lead--light">
              Outsource Shopify fulfilment UK operations and focus on growth, marketing, products, and customer
              experience. Many brands also compare{' '}
              <Link to="/amazon-fba-prep-services" className="shopify-inline-link">
                Amazon FBA prep
              </Link>{' '}
              with Shopify-first fulfilment as they scale.
            </p>
          </header>

          <ul className="shpd-benefits-grid">
            {DIY_BENEFITS.map((item) => (
              <li key={item.title} className="shpd-benefit-card">
                <span className="shpd-benefit-icon" aria-hidden>
                  <BenefitIcon name={item.icon} />
                </span>
                <h3 className="shpd-benefit-title">{item.title}</h3>
                <p className="shpd-benefit-text">
                  {item.title === 'Multichannel simplicity' ? (
                    <>
                      Centralised inventory management across Shopify,{' '}
                      <Link to="/amazon-fba-prep-services" className="shopify-inline-link">
                        Amazon
                      </Link>
                      , eBay, and{' '}
                      <Link to="/tiktok-brands-fulfilment-uk" className="shopify-inline-link">
                        TikTok Shop
                      </Link>{' '}
                      from one shared stock pool.
                    </>
                  ) : (
                    item.body
                  )}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="shpd-section shpd-section--white" aria-labelledby="sh-couriers-title">
        <div className="shpd-container">
          <header className="shpd-header">
            <span className="shpd-eyebrow">Delivery network</span>
            <h2 id="sh-couriers-title" className="shpd-title">
              Courier Partners
            </h2>
            <p className="shpd-lead">
              We blend carrier services to meet your SLA at the lowest landed cost, with clear routing rules per
              destination and weight band — aligned with our wider{' '}
              <Link to="/logistics-services" className="shopify-inline-link">
                e-commerce seller logistics
              </Link>{' '}
              network.
            </p>
          </header>

          <ul className="shpd-courier-grid">
            {COURIERS.map((c) => (
              <li key={c.name} className="shpd-courier-card">
                <span className="shpd-courier-badge" aria-hidden>
                  {c.name.charAt(0)}
                </span>
                <h3 className="shpd-courier-name">{c.name}</h3>
                <p className="shpd-courier-detail">{c.detail}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="shpd-section shpd-section--light" aria-labelledby="sh-sla-title">
        <div className="shpd-container">
          <header className="shpd-header">
            <span className="shpd-eyebrow">Performance</span>
            <h2 id="sh-sla-title" className="shpd-title">
              SLAs and Delivery Timelines
            </h2>
          </header>

          <ul className="shpd-sla-grid">
            {SLAS.map((sla) => (
              <li key={sla.label} className="shpd-sla-card">
                <span className="shpd-sla-label">{sla.label}</span>
                <p className="shpd-sla-text">{sla.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="shpd-section shpd-section--white shpd-section--last" aria-labelledby="sh-use-title">
        <div className="shpd-container">
          <header className="shpd-header">
            <span className="shpd-eyebrow">Who we help</span>
            <h2 id="sh-use-title" className="shpd-title">
              Shopify Brands We Support
            </h2>
          </header>

          <ul className="shpd-use-grid">
            {USE_CASES.map((uc) => (
              <li key={uc.title} className="shpd-use-card">
                <span className="shpd-use-dot" aria-hidden />
                <div>
                  <h3 className="shpd-use-title">{uc.title}</h3>
                  <p className="shpd-use-text">{uc.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default ShopifyProcessSections;
